import { useSaveSalesforceWebToLead } from '@hooks/useSaveSalesforceWebToLead';
import * as api from '@lib/api';
import { pattern } from '@lib/regex';
import {
    addressAtom,
    bathsAtom,
    bedroomsAtom,
    emailAtom,
    firstNameAtom,
    googlePlaceIdAtom,
    lastNameAtom,
    phoneAtom,
    rentProjectionRequestSelector,
} from '@src/state';
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import ChartPreview from '../components/chart-preview';
import { ContentView } from '../components/content-view.styled';
import { Form } from '../components/form';
import { Subtitle, Title } from '../components/form.styled';
import { LoadingSpinner } from '../components/loading-spinner';
import PageView from '../components/page-view';
import { SubmitButton } from '../components/submit-button';
import { EstimatorPath } from '../constants';
import * as styled from './gather-user-details.styled';

const GatherUserDetails: React.FC<{
    onBack: () => void;
    onNext: () => void;
    onError: () => void;
    scrollTarget: React.RefObject<HTMLDivElement>;
}> = ({ onBack, onNext, onError, scrollTarget }) => {
    const [isLoading, setIsLoading] = useState(false);
    const address = useRecoilValue(addressAtom);
    const [firstName, setFirstName] = useRecoilState(firstNameAtom);
    const [lastName, setLastName] = useRecoilState(lastNameAtom);
    const [email, setEmail] = useRecoilState(emailAtom);
    const [phone, setPhone] = useRecoilState(phoneAtom);
    const bedrooms = useRecoilValue(bedroomsAtom);
    const bathrooms = useRecoilValue(bathsAtom);
    const rentProjectionRequest = useRecoilValue(rentProjectionRequestSelector);
    const saveSalesforceWebToLead = useSaveSalesforceWebToLead();
    const googlePlaceId = useRecoilValue(googlePlaceIdAtom);
    const title = 'Unlock your revenue potential';
    const submitLabel = 'See My Estimate';

    if (!address) {
        return <Navigate to={EstimatorPath.Search} />;
    }

    const saveLeadData = async (estimateValue?: number) => {
        saveSalesforceWebToLead();
        api.emitAnalytics('step-contact-complete', {
            address,
            firstName,
            lastName,
            email,
            phoneNumber: phone,
            bedrooms,
            bathrooms,
            estimate_value: estimateValue,
        });
    };

    useEffect(() => {
        if (!isLoading) return;
        if (!googlePlaceId) return;
        if (!rentProjectionRequest) {
            setIsLoading(false);
            return;
        }
        if (email && firstName && lastName) {
            api.saveLocalLead({
                email,
                phone,
                firstName,
                lastName,
                bedrooms,
                bathrooms,
                address,
                googlePlaceId,
            });
        }

        saveLeadData().then(() => {
            onNext();
        }).catch(() => {
            onError();
        }).finally(() => {
            setIsLoading(false);
        });
    }, [isLoading]);

    const nextStepEnabled = () =>
        firstName !== undefined && lastName !== undefined && email !== undefined;

    const onSubmit = () => {
        setIsLoading(true);
    };

    const onChangeValue = (fn: (value?: string) => void) => (_valid: boolean, value?: string) => {
        fn(value);
    };

    if (!address) {
        return <></>;
    }

    return (
        <PageView dataCy="gather-user-details" scrollTarget={scrollTarget}>
            <ContentView>
                <Form onBack={onBack}>
                    <Title>{title}</Title>
                    <Subtitle bold>{address}</Subtitle>
                    <styled.Inputs>
                        <styled.FirstName
                            dataCy="input-first-name"
                            value={firstName}
                            pattern={pattern.name}
                            disabled={isLoading}
                            onEnterKeyPressed={onSubmit}
                            onChange={onChangeValue(setFirstName)}
                            placeholder="First Name"
                        />
                        <styled.LastName
                            dataCy="input-last-name"
                            value={lastName}
                            pattern={pattern.name}
                            disabled={isLoading}
                            onEnterKeyPressed={onSubmit}
                            onChange={onChangeValue(setLastName)}
                            placeholder="Last Name"
                        />
                        <styled.Email
                            dataCy="input-email"
                            value={email}
                            pattern={pattern.email}
                            disabled={isLoading}
                            onEnterKeyPressed={onSubmit}
                            onChange={onChangeValue(setEmail)}
                            placeholder="Email"
                        />
                        <styled.Phone
                            dataCy="input-phone"
                            value={phone}
                            pattern={pattern.phone}
                            required={false}
                            disabled={isLoading}
                            onEnterKeyPressed={onSubmit}
                            onChange={onChangeValue(setPhone)}
                            placeholder="Phone (optional)"
                        />
                        <styled.Submit>
                            {isLoading ? (
                                <LoadingSpinner size={56} />
                            ) : (
                                <SubmitButton
                                    label={submitLabel}
                                    disabled={isLoading || !nextStepEnabled()}
                                    onClick={onSubmit}
                                    spinnerSize={56}
                                />
                            )}
                        </styled.Submit>
                        <styled.Terms>
                            By providing your number, you consent to receive automated text messages
                            from Vacasa and agree to our{' '}
                            <a
                                href="https://www.vacasa.com/policies"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Privacy Policy
                            </a>
                            . Message/data rates may apply. Consent to receive messages is not
                            required to receive services.
                        </styled.Terms>
                    </styled.Inputs>
                </Form>
                <ChartPreview title="Calculating income..." />
            </ContentView>
        </PageView>
    );
};

export default GatherUserDetails;
